import React, { Component } from 'react'
import { withRouter, Redirect } from 'react-router-dom'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { graphql, gql, compose } from 'react-apollo'
import {
  companySettingFragment,
  companyPaymentFragment,
  companyQuestionFragment,
  companyFragment,
  viewerFragment,
  userErrorsFragment,
} from 'fragments'
import { message } from 'antd'
import { setUIState, containerIds } from 'modules/ui'
import { setCompany, setQuestions } from 'modules/company'
import { imagePath } from 'frontend_core/dist/helpers/image'
import SMConst from 'frontend_core/dist/constants/sm-const'
import Path from 'routes/path'
import ScrollControlView from 'components/atoms/ScrollControlView'
import PopupWrapper from 'components/atoms/PopupWrapper'
import BusinessLayout from 'components/layouts/BusinessLayout'
import SettingForm from 'components/organisms/setting/SettingForm'
import QuestionForm from 'components/organisms/setting/QuestionForm'
import PaidExaminationStatus from 'components/organisms/setting/PaidExaminationStatus'
import ConfirmRegisterPopup from 'components/business/ConfirmRegisterPopup'
import SavedPopup from 'components/business/SavedPopup'
import PaidExaminationPopup from 'components/business/PaidExaminationPopup'
import RePaidExaminationPopup from 'components/business/RePaidExaminationPopup'

class SettingContainer extends Component {
  constructor(props) {
    super(props)

    const { actions, data } = this.props
    const company = data.company || {}
    actions.setCompany({
      ...company,
      corpName: company.name,
    })

    actions.setUIState(containerIds.BUSINESS_SETTING, {
      isPaidUpdate: !!company.paidCustomer,
      showSettingForm: true,
    })

    this.state = {
      isCreate: this.props.company.questions.results.length < 5,
    }
  }

  changeCompanyField = (fieldName, value) => {
    const { actions, company, ui } = this.props

    company[fieldName] = value
    actions.setCompany(company)

    const uiErrors = (ui.errors || []).filter(e => e.field !== fieldName)
    actions.setUIState(containerIds.BUSINESS_SETTING, {
      errors: uiErrors,
    })

    switch (fieldName) {
      case 'businessTaxId':
      case 'name':
      case 'corpNameKana':
      case 'ceoFamilyName':
      case 'ceoGivenName':
      case 'ceoFamilyNameKana':
      case 'ceoGivenNameKana':
      case 'postalCode':
      case 'state':
      case 'city':
      case 'address1':
      case 'tel':
        actions.setUIState(containerIds.BUSINESS_SETTING, {
          isChangedCompany: true,
        })
        break
      default:
        break
    }
  }

  updateCompany = async () => {
    const { actions, company, updateCompany, ui } = this.props

    const companyInput = {
      id: company.id,
      name: company.name,
      corpNameKana: company.corpNameKana,
      contactPersonFamilyName: company.contactPersonFamilyName,
      contactPersonGivenName: company.contactPersonGivenName,
      contactPersonFamilyNameKana: company.contactPersonFamilyNameKana,
      contactPersonGivenNameKana: company.contactPersonGivenNameKana,
      ceoFamilyName: company.ceoFamilyName,
      ceoGivenName: company.ceoGivenName,
      ceoFamilyNameKana: company.ceoFamilyNameKana,
      ceoGivenNameKana: company.ceoGivenNameKana,
      department: company.department,
      businessTaxId: company.businessTaxId,
      postalCode: company.postalCode,
      state: company.state,
      city: company.city,
      address1: company.address1,
      address2: company.address2,
      email: company.email,
      profileImage: company.profileImage,
      tel: company.tel,
      selectedPaymentId: parseInt(company.selectedPaymentId, 10) || null,
      paymentMethod: parseInt(company.paymentMethod, 10) || null,
    }

    try {
      actions.setUIState(containerIds.BUSINESS_SETTING, {
        mutateLoading: true,
      })

      const res = await updateCompany({ company: companyInput })
      const userErrors = res.data.updateCompany.userErrors || []

      actions.setUIState(containerIds.BUSINESS_SETTING, {
        errors: userErrors,
        isShowingConfirm: false,
      })

      if (userErrors.length) {
        message.warning(i18n.t('page.shared.inputInformationIncomplete'))
      } else {
        actions.setCompany(res.data.updateCompany.company)
        if (company.paymentMethod === SMConst.PaymentMethod.Paid) {
          if (ui.isPaidUpdate) {
            actions.setUIState(containerIds.BUSINESS_SETTING, {
              isShowingRePaidPopup: true,
            })
          } else {
            actions.setUIState(containerIds.BUSINESS_SETTING, {
              isShowingPaidPopup: true,
            })
          }
        } else {
          actions.setUIState(containerIds.BUSINESS_SETTING, {
            isShowingSavedPopup: true,
          })
        }
      }
    } catch (e) {
      message.warning(i18n.t('error.500.title'))
    } finally {
      actions.setUIState(containerIds.BUSINESS_SETTING, {
        mutateLoading: false,
      })
    }
  }

  onClickSaveQuestions = () => {
    this.state.isCreate
      ? this.createCompanyQuestions()
      : this.updateCompanyQuestions()
  }

  createCompanyQuestions = async () => {
    const { actions, company, createCompanyQuestions, ui } = this.props

    const questions = company.questions.results.map(question => ({
      title: question.title,
      description: question.description,
      example: question.example,
      isRequired: question.isRequired,
      isVisible: question.isVisible,
    }))

    const questionsInput = {
      companyUsername: company.username,
      companyQuestions: questions,
    }

    try {
      actions.setUIState(containerIds.BUSINESS_SETTING, {
        mutateLoading: true,
      })

      const res = await createCompanyQuestions(questionsInput)
      const userErrors = res.data.createCompanyQuestions.userErrors || []
      const errors = res.errors || []
      actions.setUIState(containerIds.BUSINESS_SETTING, {
        errors: userErrors,
        isShowingConfirm: false,
      })

      if (userErrors.length) {
        message.warning(i18n.t('page.shared.inputInformationIncomplete'))
      } else if (errors.length) {
        errors.map(error => {
          if (error.message === i18n.t('page.shared.permissionDenied'))
            message.warning(i18n.t('page.shared.permissionDenied'))
        })
      } else {
        const questionState = {
          questions: {
            results: res.data.createCompanyQuestions.createdCompanyQuestions,
          },
        }
        actions.setQuestions(questionState)
        actions.setUIState(containerIds.BUSINESS_SETTING, {
          isShowingSavedPopup: true,
        })
        this.setState({ isCreate: false })
      }
    } catch (e) {
      message.warning(i18n.t('error.500.title'))
    } finally {
      actions.setUIState(containerIds.BUSINESS_SETTING, {
        mutateLoading: false,
      })
    }
  }

  updateCompanyQuestions = async () => {
    const { actions, company, updateCompanyQuestions, ui } = this.props

    const questions = company.questions.results.map(question => ({
      id: question.id,
      title: question.title,
      description: question.description,
      example: question.example,
      isRequired: question.isRequired,
      isVisible: question.isVisible,
    }))

    const questionsInput = {
      companyUsername: company.username,
      companyQuestions: questions,
    }

    try {
      actions.setUIState(containerIds.BUSINESS_SETTING, {
        mutateLoading: true,
      })

      const res = await updateCompanyQuestions(questionsInput)
      const userErrors = res.data.updateCompanyQuestions.userErrors || []
      const errors = res.errors || []
      actions.setUIState(containerIds.BUSINESS_SETTING, {
        errors: userErrors,
        isShowingConfirm: false,
      })

      if (userErrors.length) {
        message.warning(i18n.t('page.shared.inputInformationIncomplete'))
      } else if (errors.length) {
        errors.map(error => {
          if (error.message === i18n.t('page.shared.permissionDenied'))
            message.warning(i18n.t('page.shared.permissionDenied'))
        })
      } else {
        const questionState = {
          questions: {
            results: res.data.updateCompanyQuestions.updatedCompanyQuestions,
          },
        }
        actions.setQuestions(questionState)
        actions.setUIState(containerIds.BUSINESS_SETTING, {
          isShowingSavedPopup: true,
        })
      }
    } catch (e) {
      message.warning(i18n.t('error.500.title'))
    } finally {
      actions.setUIState(containerIds.BUSINESS_SETTING, {
        mutateLoading: false,
      })
    }
  }

  render() {
    const { ui, actions, company, location, history, data } = this.props

    const isShowingPopup = ui.isShowingConfirm || ui.isShowingPaidPopup

    if (!data.viewer || !data.company) {
      return <Redirect to={Path.signup()} />
    }

    return (
      <BusinessLayout
        title={i18n.t('server.business.setting.title')}
        description={i18n.t('server.business.setting.description')}
        {...this.props}
      >
        <ScrollControlView isLock={isShowingPopup}>
          <div className="site-business__main">
            <div className="container">
              <PaidExaminationStatus
                {...this.props}
                hasErrorMessage={(ui.errors || []).length > 0}
              />
              <h1 className="business__title">
                {i18n.t('page.setting.sectionTitle')}
              </h1>
              {ui.showSettingForm ? (
                <SettingForm
                  {...this.props}
                  onChangeCompany={this.changeCompanyField}
                  onClickSave={() => {
                    if (company.paidCustomer === null) {
                      actions.setUIState(containerIds.BUSINESS_SETTING, {
                        isShowingConfirm: true,
                      })
                    } else {
                      this.updateCompany()
                    }
                  }}
                />
              ) : (
                <QuestionForm
                  {...this.props}
                  onClickSave={this.onClickSaveQuestions}
                />
              )}
            </div>
          </div>
        </ScrollControlView>
        {ui.isShowingConfirm && (
          <PopupWrapper>
            <div className="site-main site-business__main">
              <ConfirmRegisterPopup
                {...this.props}
                paidForm={company}
                stateText={company.stateText}
                onClickBack={() =>
                  actions.setUIState(containerIds.BUSINESS_SETTING, {
                    isShowingConfirm: false,
                  })
                }
                onClickRegister={this.updateCompany}
              />
            </div>
          </PopupWrapper>
        )}
        {ui.isShowingPaidPopup && (
          <PopupWrapper>
            <PaidExaminationPopup
              onClickBack={() => {
                actions.setUIState(containerIds.BUSINESS_SETTING, {
                  isShowingPaidPopup: false,
                })
              }}
              onClickContinue={() => {
                actions.setUIState(containerIds.BUSINESS_SETTING, {
                  isShowingPaidPopup: false,
                })
              }}
            />
          </PopupWrapper>
        )}
        {ui.isShowingSavedPopup && (
          <PopupWrapper>
            <SavedPopup
              onClickBack={() => {
                actions.setUIState(containerIds.BUSINESS_SETTING, {
                  isShowingSavedPopup: false,
                })
              }}
              onClickContinue={() => {
                actions.setUIState(containerIds.BUSINESS_SETTING, {
                  isShowingSavedPopup: false,
                  showSettingForm: true,
                })
                window.scrollTo(0, 0)
              }}
            />
          </PopupWrapper>
        )}
        {ui.isShowingRePaidPopup && (
          <PopupWrapper>
            <RePaidExaminationPopup
              onClickBack={() => {
                actions.setUIState(containerIds.BUSINESS_SETTING, {
                  isShowingRePaidPopup: false,
                })
              }}
              onClickToPaid={() => {
                actions.setUIState(containerIds.BUSINESS_SETTING, {
                  isShowingRePaidPopup: false,
                })
                window.open('https://paid.jp/v/do/login', '_blank')
              }}
            />
          </PopupWrapper>
        )}
      </BusinessLayout>
    )
  }
}

const updateCompany = gql`
  mutation updateCompany($input: UpdateCompanyInput!) {
    updateCompany(input: $input) {
      company {
        ${companySettingFragment}
      }
      userErrors {
        ${userErrorsFragment}
      }
    }
  }
`

const createCompanyPayment = gql`
  mutation createCompanyPayment($input: CreateCompanyPaymentInput!) {
    createCompanyPayment(input: $input) {
      company {
        payments {
          results {
            ... on CompanyPayment {
              ${companyPaymentFragment}
            }
          }
        }
      }
      userErrors {
        ${userErrorsFragment}
      }
    }
  }
`

const deleteCompanyPayment = gql`
  mutation deleteCompanyPayment($input: DeleteCompanyPaymentInput!) {
    deleteCompanyPayment(input: $input) {
      deletedCompanyPaymentId
      userErrors {
        ${userErrorsFragment}
      }
    }
  }
`

const createCompanyQuestions = gql`
  mutation createCompanyQuestions($input: CreateCompanyQuestionsInput!) {
    createCompanyQuestions(input: $input) {
      createdCompanyUsername
      createdCompanyQuestions {
        id
        title
        description
        example
        isRequired
        isVisible
        companyId
      }
      userErrors {
        index
        field
        resource
        message
      }
    }
  }
`

const updateCompanyQuestions = gql`
  mutation updateCompanyQuestions($input: UpdateCompanyQuestionsInput!) {
    updateCompanyQuestions(input: $input) {
      updatedCompanyUsername
      updatedCompanyQuestions {
        id
        title
        description
        example
        isRequired
        isVisible
        companyId
      }
      userErrors {
        index
        field
        resource
        message
      }
    }
  }
`

const withMutation = compose(
  graphql(updateCompany, {
    props: ({ mutate }) => ({
      updateCompany: input => mutate({ variables: { input } }),
    }),
  }),
  graphql(createCompanyPayment, {
    props: ({ mutate }) => ({
      createCompanyPayment: input => mutate({ variables: { input } }),
    }),
  }),
  graphql(createCompanyQuestions, {
    props: ({ mutate }) => ({
      createCompanyQuestions: input => mutate({ variables: { input } }),
    }),
  }),
  graphql(updateCompanyQuestions, {
    props: ({ mutate }) => ({
      updateCompanyQuestions: input => mutate({ variables: { input } }),
    }),
  }),
  graphql(deleteCompanyPayment, {
    props: ({ mutate }) => ({
      deleteCompanyPayment: input => mutate({ variables: { input } }),
    }),
  }),
)(SettingContainer)

const withData = graphql(
  gql`
  query BusinessSettingContainer($username: String!) {
    company(username: $username) {
      ${companySettingFragment}
      payments {
        results {
          ... on CompanyPayment {
            ${companyPaymentFragment}
          }
        }
      }
      questions {
        results {
          ${companyQuestionFragment}
        }
      }
    }
    viewer {
      ${viewerFragment}
      companies {
        results {
          ... on Company {
            ${companyFragment}
          }
        }
      }
    }
  }
`,
  {
    options: props => ({
      notifyOnNetworkStatusChange: true,
      variables: {
        username: props.match.params.username,
      },
    }),
  },
)(withMutation)

function mapStateToProps(state) {
  return state
}

function mapDispatchToProps(dispatch) {
  const bindActions = {
    setUIState,
    setCompany,
    setQuestions,
  }
  return { actions: bindActionCreators(bindActions, dispatch) }
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withData),
)
